import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface ModalPanelProps {
  /**
   * Si el modal está abierto
   */
  isOpen: boolean;

  /**
   * Título del modal
   */
  title: string;

  /**
   * Subtítulo opcional debajo del título
   */
  subtitle?: string;

  /**
   * Callback al cerrar
   */
  onClose: () => void;

  /**
   * Contenido principal del modal
   */
  children: ReactNode;

  /**
   * Tamaño del modal
   * @default "large"
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge';

  /**
   * Si se debe cerrar al hacer click fuera
   * @default true
   */
  closeOnClickOutside?: boolean;

  /**
   * Si se debe cerrar al presionar ESC
   * @default true
   */
  closeOnEsc?: boolean;

  /**
   * Acciones en el header (antes del botón cerrar) — ej: toggle A4/Ticket
   */
  headerActions?: ReactNode;

  /**
   * Contenido a la izquierda del footer — ej: resumen de ítems, totales
   */
  footerInfo?: ReactNode;

  /**
   * Acciones a la derecha del footer — ej: botones Imprimir, Descargar
   */
  footerActions?: ReactNode;

  /**
   * Si es true, deshabilita el botón X del header para evitar cierre accidental
   * mientras se procesa una operación crítica
   * @default false
   */
  disableClose?: boolean;
}

export const ModalPanel: React.FC<ModalPanelProps> = ({
  isOpen,
  title,
  subtitle,
  onClose,
  children,
  size = 'large',
  closeOnClickOutside = true,
  closeOnEsc = true,
  headerActions,
  footerInfo,
  footerActions,
  disableClose = false,
}) => {
  // Cerrar con ESC
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  // Prevenir scroll del body cuando está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (typeof document === 'undefined') return null;

  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl',
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closeOnClickOutside) {
      onClose();
    }
  };

  const hasFooter = footerInfo || footerActions;

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className={`w-full ${sizeClasses[size]} bg-surface-1 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-2.5 border-b border-default flex-shrink-0">
          <div>
            <h2 className="font-display text-h4 text-primary">{title}</h2>
            {subtitle && (
              <p className="text-ui-sm text-tertiary mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {headerActions && (
              <div className="flex items-center">{headerActions}</div>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={disableClose}
              className="rounded-lg p-2 text-tertiary transition-colors hover:bg-surface-hover hover:text-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {children}
        </div>

        {/* Footer */}
        {hasFooter && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-default flex-shrink-0">
            <div className="text-ui-sm text-tertiary">
              {footerInfo}
            </div>
            {footerActions && (
              <div className="flex items-center gap-3">
                {footerActions}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

ModalPanel.displayName = 'ModalPanel';

export default ModalPanel;


